/**
 * Coach's Tactics Board — Match Lab.
 *
 * Takes a short segment from the current playhead and turns the tracking into
 * a deck of still "cards", each a snapshot of all 22 players and the ball on
 * the pitch, with arrows showing how the shape moves on into the next card. A
 * coach steps (or auto-plays) through the deck to explain an attack, a press,
 * a shape change — PowerPoint for a moment of the match.
 *
 * Three ways to decide where the cards fall:
 *  - auto   — the ball's possession changes (each new player who settles it =
 *             a pass/turnover) plus known shots/goals/set-pieces become cards.
 *  - manual — scrub the window and click Add card at each moment you want.
 *  - even   — fixed time intervals (the simple fallback).
 *
 * Frontend only: positions come from the same opsqa tracking-frames endpoint
 * the spatial view reads (cm from the centre spot, y up-positive, team 1 =
 * home / 0 = away). No new backend.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  CSSProperties, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent,
} from 'react'

import { api } from '../lib/api'
import {
  OWNER_RADIUS_CM, controlGrid, openLanes, pressureCm, sequenceMetrics,
  settledOwners, teamBlock, type SequenceMetrics,
} from './tacticsBoardAnalytics'
import {
  PITCH_L, PITCH_W, VIEW_BOX, VIEW_H, VIEW_W, cardInner, svgPos, wrapCaption,
} from './tacticsCardSvg'
import type { Anno, Overlay, RenderCard, RenderTeams } from './tacticsCardSvg'

// The card renderer — constants, the Anno type, wrapCaption — lives in
// tacticsCardSvg, the single source shared with the PNG export and the viewer.
export type { Anno }
export { wrapCaption }

export type TacPlayer = { team: number; jersey: number; x: number; y: number }
export type TacFrame = {
  frame: number
  players: TacPlayer[]
  ball: { x: number; y: number } | null
}
export type TacSlide = TacFrame & { caption: string }
export type MoveFilter = 'both' | 'home' | 'away'
export type BoardMode = 'auto' | 'manual' | 'even'

/** Which overlays are painted on the pitch. */
export type OverlayCfg = { control: boolean; block: boolean; lanes: boolean; pressure: boolean }
/** Telestration tools; 'spotlight' highlights clicked players across the deck. */
export type DrawTool = 'none' | 'arrow' | 'zone' | 'text' | 'spotlight'
const NO_ANNOS: Anno[] = []

/** A moment the deck stops on. `idx` is into the fetched window. */
export type KeyKind =
  'start' | 'end' | 'pass' | 'turnover' | 'shift' | 'shot' | 'goal' | 'set_piece' | 'manual'
export type KeyFrame = { idx: number; kind: KeyKind }
export type Card = KeyFrame & { caption: string }

/** A match event to overlay as a forced card, from Match Lab's timeline. */
export type TacEvent = { frame_idx: number; type: string }

/** The tracking-frames body: players carry team/jersey, referees are team
 *  3/4 and are dropped. Coordinates stay in centimetres here. */
type RawFrame = {
  frame: number
  players?: { team: number; jersey: number; x: number; y: number }[]
  ball?: { x: number; y: number } | null
}

/** cm-from-centre → SVG pitch metres as {x,y} (the renderer's svgPos returns a
 *  tuple; this is the board-facing shape). */
export function tacticsSvgPos(xCm: number, yCm: number): { x: number; y: number } {
  const [x, y] = svgPos(xCm, yCm)
  return { x, y }
}

/** Keep only the two playing teams; drop referees (team 3/4) and anything
 *  without a jersey, so a card is 22 players and a ball, not the crew. */
function cleanFrame(f: RawFrame): TacFrame {
  const players = (f.players ?? []).filter(
    (p) => (p.team === 0 || p.team === 1) && p.jersey != null)
  return { frame: f.frame, players, ball: f.ball ?? null }
}

export type Move = {
  team: number
  jersey: number
  from: { x: number; y: number }
  to: { x: number; y: number }
}

/** How each player moves from one card to the next, for players on the pitch
 *  in both and who travelled more than `minCm` — a standstill is tracking
 *  jitter, not a run, and an arrow on it only adds noise. */
export function playerMovements(
  from: TacFrame, to: TacFrame, minCm = 150): Move[] {
  const dest = new Map<string, TacPlayer>()
  for (const p of to.players) dest.set(`${p.team}_${p.jersey}`, p)
  const out: Move[] = []
  for (const p of from.players) {
    const q = dest.get(`${p.team}_${p.jersey}`)
    if (!q) continue
    if (Math.hypot(q.x - p.x, q.y - p.y) < minCm) continue
    out.push({ team: p.team, jersey: p.jersey,
               from: { x: p.x, y: p.y }, to: { x: q.x, y: q.y } })
  }
  return out
}

/** The player nearest the ball within `radiusCm`, or null when it is loose. */
function nearestToBall(f: TacFrame, radiusCm = OWNER_RADIUS_CM): TacPlayer | null {
  if (!f.ball) return null
  let best: TacPlayer | null = null
  let bestD = radiusCm
  for (const p of f.players) {
    const d = Math.hypot(p.x - f.ball.x, p.y - f.ball.y)
    if (d < bestD) { bestD = d; best = p }
  }
  return best
}

/** A one-line account of what happened between the previous card and this one:
 *  who moved the ball to whom, how far it and the biggest runner travelled, and
 *  the event if any. Factual and direction-agnostic — no claim the tracking
 *  cannot back. Recomputed live, so removing a card re-describes the larger
 *  gap it now spans. */
export function describeTransition(
  prev: TacFrame, cur: TacFrame, kind: KeyKind, fps: number,
  homeName: string, awayName: string): string {
  const team = (t: number) => (t === 1 ? homeName : awayName)
  const was = nearestToBall(prev)
  const has = nearestToBall(cur)
  const ballM = prev.ball && cur.ball
    ? Math.hypot(cur.ball.x - prev.ball.x, cur.ball.y - prev.ball.y) / 100 : 0

  // The biggest run among players on the pitch in both frames.
  const pos = new Map<string, TacPlayer>()
  for (const p of cur.players) pos.set(`${p.team}_${p.jersey}`, p)
  let run = { jersey: 0, dist: 0 }
  for (const p of prev.players) {
    const q = pos.get(`${p.team}_${p.jersey}`)
    if (!q) continue
    const d = Math.hypot(q.x - p.x, q.y - p.y)
    if (d > run.dist) run = { jersey: p.jersey, dist: d }
  }

  // The lead follows the card's KIND, so the narration never contradicts the
  // card's label (a turnover card always reads as a turnover, never "holds").
  // was/has supply the jersey numbers; they do not re-decide pass vs turnover.
  let lead: string
  if (kind === 'goal') lead = `GOAL${has ? ' — ' + team(has.team) : ''}`
  else if (kind === 'shot') lead = `Shot${has ? ' — ' + team(has.team) : ''}`
  else if (kind === 'set_piece') lead = 'Set piece'
  else if (kind === 'shift') lead = 'Shape shifts'
  else if (kind === 'turnover') lead = has ? `${team(has.team)} win it back` : 'Turnover'
  else if (kind === 'pass') {
    // "A → B" only for a genuine pass: same team, two different players. A
    // single-frame nearest can be an opponent (wrong team) or the same holder
    // across both frames, so a pass card must emit neither a cross-team pass
    // nor a phantom "9 → 9" — it falls back to naming who holds it.
    lead = was && has && was.team === has.team && was.jersey !== has.jersey
      ? `${team(has.team)} ${was.jersey} → ${has.jersey}`
      : has ? `${team(has.team)} ${has.jersey} holds` : 'Pass'
  } else if (was && has && was.team !== has.team) lead = `${team(has.team)} win it back`
  else if (was && has && was.jersey !== has.jersey) lead = `${team(has.team)} ${was.jersey} → ${has.jersey}`
  else if (has) lead = `${team(has.team)} ${has.jersey} holds`
  else lead = 'Ball loose'

  const bits: string[] = []
  if (ballM >= 3) bits.push(`ball ${Math.round(ballM)}m`)
  if (run.dist >= 300) bits.push(`#${run.jersey} runs ${Math.round(run.dist / 100)}m`)
  const secs = (cur.frame - prev.frame) / fps
  return `${lead}${bits.length ? ' — ' + bits.join(', ') : ''} (${secs.toFixed(1)}s)`
}

/** A promoted card: a self-contained snapshot — the players and ball, the
 *  transition into the next card, the coach's drawings and words — so the
 *  published page never re-reads tracking (docs/tactics-deck-promote-plan.md). */
export type DeckCard = {
  kind: KeyKind
  caption: string
  commentary: string
  players: TacPlayer[]
  ball: { x: number; y: number } | null
  moves: Move[]
  ballMove: { from: { x: number; y: number }; to: { x: number; y: number } } | null
  annos: Anno[]
}
/** The portable deck document — what Save & Promote stores and the club viewer
 *  renders. v1 of the format; positions are in cm from the centre spot. */
export type DeckDoc = {
  v: 1
  game: number
  title: string
  teams: { home: { name: string; color: string }; away: { name: string; color: string } }
  display: { flipH: boolean; flipV: boolean }
  cards: DeckCard[]
}

/** Snapshot the live board state into a portable deck document. Pure — the
 *  cards' positions, arrows and commentary are frozen here, not re-derived. */
export function serializeDeck(input: {
  gameId: number
  title: string
  homeName: string; awayName: string; homeColor: string; awayColor: string
  flipH: boolean; flipV: boolean
  cards: Card[]
  frames: TacFrame[]
  fps: number
  moves: MoveFilter
  annos: Record<number, Anno[]>
}): DeckDoc {
  const { gameId, title, homeName, awayName, homeColor, awayColor,
          flipH, flipV, cards, frames, fps, moves, annos } = input
  const deckCards: DeckCard[] = cards.map((c, i) => {
    const fr = frames[c.idx]!
    const nx = cards[i + 1] ? frames[cards[i + 1]!.idx] : undefined
    const mv = nx
      ? playerMovements(fr, nx).filter((m) =>
          moves === 'both' || (moves === 'home') === (m.team === 1))
      : []
    const ballMove = nx?.ball && fr.ball
      && Math.hypot(nx.ball.x - fr.ball.x, nx.ball.y - fr.ball.y) >= 100
      ? { from: { x: fr.ball.x, y: fr.ball.y }, to: { x: nx.ball.x, y: nx.ball.y } } : null
    const commentary = i === 0 ? 'Starting shape.'
      : describeTransition(frames[cards[i - 1]!.idx]!, fr, c.kind, fps, homeName, awayName)
    return { kind: c.kind, caption: c.caption, commentary,
             players: fr.players, ball: fr.ball, moves: mv, ballMove,
             annos: annos[c.idx] ?? [] }
  })
  return {
    v: 1, game: gameId, title,
    teams: { home: { name: homeName, color: homeColor },
             away: { name: awayName, color: awayColor } },
    display: { flipH, flipV },
    cards: deckCards,
  }
}

/** Where the ball settles with a NEW owner — a pass (same team) or a turnover
 *  (the other team). A "settled" possession is a run of ≥ `minHold` frames
 *  with the same owner; loose-ball gaps between touches are ignored. */
export function possessionChanges(
  frames: TacFrame[], minHold: number, radiusCm = OWNER_RADIUS_CM): KeyFrame[] {
  const settled = settledOwners(frames, minHold, radiusCm)
  const out: KeyFrame[] = []
  for (let k = 1; k < settled.length; k++) {
    const sameTeam = settled[k]!.owner.split('_')[0] === settled[k - 1]!.owner.split('_')[0]
    out.push({ idx: settled[k]!.idx, kind: sameTeam ? 'pass' : 'turnover' })
  }
  return out
}

/** The array index of the frame nearest a game-frame number, or -1 if it falls
 *  outside the window — the tracking response can have gaps, so an event's card
 *  must be found by frame, not by assuming index == frame − first. */
export function nearestFrameIndex(frames: TacFrame[], frame: number): number {
  if (frames.length === 0) return -1
  if (frame < frames[0]!.frame || frame > frames[frames.length - 1]!.frame) return -1
  let best = 0, bestD = Infinity
  for (let i = 0; i < frames.length; i++) {
    const d = Math.abs(frames[i]!.frame - frame)
    if (d < bestD) { bestD = d; best = i }
  }
  return best
}

// A shot/goal/set-piece is a bigger deal than a routine pass; ends anchor the
// deck. Used to resolve collisions and to thin when there are too many.
const RANK: Record<KeyKind, number> = {
  goal: 6, shot: 5, set_piece: 5, start: 4, end: 4, turnover: 3, manual: 3,
  shift: 3, pass: 2,
}

// The beats that are "filler" relative to events/ends — thinned first, kept
// when the deck is capped.
const THINNABLE = (k: KeyKind) => k === 'pass' || k === 'turnover' || k === 'shift'

/** Average distance the players on the pitch in both frames have moved. */
function meanDisplacement(a: TacFrame, b: TacFrame): number {
  const pos = new Map<string, TacPlayer>()
  for (const p of b.players) pos.set(`${p.team}_${p.jersey}`, p)
  let sum = 0, n = 0
  for (const p of a.players) {
    const q = pos.get(`${p.team}_${p.jersey}`)
    if (!q) continue
    sum += Math.hypot(q.x - p.x, q.y - p.y); n++
  }
  return n ? sum / n : 0
}

/** Frames where the shape has shifted collectively since the last marked one —
 *  a line stepping up, a block sliding across, a big switch — even with no
 *  change of possession. Each shift resets the reference, so they space out. */
export function shapeShiftFrames(
  frames: TacFrame[], meanThreshCm: number, minGap: number): number[] {
  const out: number[] = []
  let ref = 0
  for (let i = 1; i < frames.length; i++) {
    if (i - ref < minGap) continue
    if (meanDisplacement(frames[ref]!, frames[i]!) >= meanThreshCm) {
      out.push(i); ref = i
    }
  }
  return out
}

/** Drop keys closer together than `minGap`, keeping the higher-ranked one, so
 *  a pass detected one frame off a shot does not make two cards. */
function mergeClose(keys: KeyFrame[], minGap: number): KeyFrame[] {
  const out: KeyFrame[] = []
  for (const k of keys) {
    const prev = out[out.length - 1]
    if (prev && k.idx - prev.idx < minGap) {
      if (RANK[k.kind] > RANK[prev.kind]) out[out.length - 1] = k
    } else out.push(k)
  }
  return out
}

/** Keep at most `max` cards: never drop an event or an end; thin the routine
 *  passes toward the preferred end — so working back from a goal we keep the
 *  build-up to it (prefer 'end') rather than stopping short of the good stuff,
 *  and working from a kick-off we keep the opening (prefer 'start'). */
function thinToMax(keys: KeyFrame[], max: number, prefer: 'start' | 'end'): KeyFrame[] {
  const must = keys.filter((k) => !THINNABLE(k.kind))
  const filler = keys.filter((k) => THINNABLE(k.kind))
  if (must.length >= max) {
    const keep = new Set(
      [...must].sort((a, b) => RANK[b.kind] - RANK[a.kind])
        .slice(0, max).map((k) => k.idx))
    return keys.filter((k) => keep.has(k.idx))
  }
  const budget = max - must.length
  const picks = new Set(
    [...filler]
      .sort((a, b) => (prefer === 'end' ? b.idx - a.idx : a.idx - b.idx))
      .slice(0, budget).map((k) => k.idx))
  return keys.filter((k) => !THINNABLE(k.kind) || picks.has(k.idx))
}

/** The auto deck: start, end, every possession change, and every event in the
 *  window — de-duped, merged when near, capped at `max`. `events` carry idx
 *  already relative to the window. `prefer` is which end to keep when there
 *  are more moments than the cap allows. */
export function detectKeyFrames(
  frames: TacFrame[], events: KeyFrame[], fps: number, max: number,
  prefer: 'start' | 'end' = 'end', minHoldS = 0.3): KeyFrame[] {
  if (frames.length === 0) return []
  const last = frames.length - 1
  const minHold = Math.max(3, Math.round(minHoldS * fps))
  const cand = new Map<number, KeyKind>()
  cand.set(0, 'start')
  cand.set(last, 'end')
  for (const { idx, kind } of possessionChanges(frames, minHold)) {
    if (!cand.has(idx)) cand.set(idx, kind)
  }
  // A big collective move with no pass is still a beat worth a card.
  for (const idx of shapeShiftFrames(frames, 700, Math.max(minHold, Math.round(fps)))) {
    if (!cand.has(idx)) cand.set(idx, 'shift')
  }
  for (const e of events) {
    if (e.idx < 0 || e.idx > last) continue
    const cur = cand.get(e.idx)
    if (cur === undefined || RANK[e.kind] > RANK[cur]) cand.set(e.idx, e.kind)
  }
  let keys = [...cand.entries()]
    .map(([idx, kind]) => ({ idx, kind }))
    .sort((a, b) => a.idx - b.idx)
  keys = mergeClose(keys, minHold)
  // Anchor both ends of the window first, then thin — so the final deck is
  // never more than `max` cards (re-adding the ends after the cap used to let
  // it reach max + 2). A deck with no last card loses the end of the move.
  if (!keys.some((k) => k.idx === 0)) keys.unshift({ idx: 0, kind: 'start' })
  if (last > 0 && !keys.some((k) => k.idx === last)) keys.push({ idx: last, kind: 'end' })
  keys.sort((a, b) => a.idx - b.idx)
  if (keys.length > max) keys = thinToMax(keys, max, prefer)
  return keys.sort((a, b) => a.idx - b.idx)
}

/** Evenly spaced indices, always including the first and last — the simple
 *  "even" mode and a fallback when detection finds nothing. */
export function evenKeyFrames(len: number, n: number): KeyFrame[] {
  if (len === 0) return []
  const m = Math.max(1, Math.min(n, len))
  const out: KeyFrame[] = []
  const seen = new Set<number>()
  for (let i = 0; i < m; i++) {
    const idx = m === 1 ? 0 : Math.round((i * (len - 1)) / (m - 1))
    if (seen.has(idx)) continue
    seen.add(idx)
    out.push({ idx, kind: i === 0 ? 'start' : i === m - 1 ? 'end' : 'pass' })
  }
  return out
}

/** Map a Match Lab event type to a card kind. Only goal/shot/set_piece are
 *  emitted by match-lab-info; anything else is treated as a generic shot. */
export function eventKind(type: string): KeyKind {
  if (type === 'goal') return 'goal'
  if (type === 'set_piece') return 'set_piece'
  if (type === 'shot') return 'shot'
  return 'shot'
}

export type Detail = 'touch' | 'pass' | 'settled'
// How long the ball must settle to count as a touch — smaller = more cards.
const detailHoldS = (d: Detail) => (d === 'touch' ? 0.15 : d === 'settled' ? 0.6 : 0.3)
// Auto shows every beat up to a safety ceiling (then thins toward the kept
// end) so a long, busy clip cannot render an unbounded wall of cards; you
// still curate by removing.
const AUTO_MAX = 40

const KIND_META: Record<KeyKind, { glyph: string; color: string; label: string }> = {
  goal: { glyph: '⚽', color: '#f4c04e', label: 'Goal' },
  shot: { glyph: '◎', color: '#f4c04e', label: 'Shot' },
  set_piece: { glyph: '⚑', color: '#f4c04e', label: 'Set piece' },
  pass: { glyph: '·', color: '#8a9099', label: 'Pass' },
  turnover: { glyph: '⟲', color: '#e08a8a', label: 'Turnover' },
  shift: { glyph: '⇄', color: '#79b0e0', label: 'Shift' },
  manual: { glyph: '➕', color: '#8ab4ff', label: 'Manual card' },
  start: { glyph: '▶', color: '#8ab4ff', label: 'Start' },
  end: { glyph: '■', color: '#8ab4ff', label: 'End' },
}

const MAX_FRAMES = 1800 // the endpoint's own cap: a minute at 30 fps.
const NO_EVENTS: TacEvent[] = [] // stable default — a fresh [] would loop renders.
const PANEL_W = 460 // px — panel width, and the page shift when pinned.

export function CoachTacticsBoard({
  gameId, startFrame, fps, homeColor, awayColor, homeName, awayName,
  events = NO_EVENTS, onSeek, onClose,
}: {
  gameId: number
  /** Segment start — seeded from the playhead when the board opens. */
  startFrame: number
  fps: number
  homeColor: string
  awayColor: string
  homeName: string
  awayName: string
  /** Match events (goals/shots/set-pieces) to overlay as forced cards. */
  events?: TacEvent[]
  /** Seek the Match Lab video to a frame — clicking a card jumps there. */
  onSeek?: (frame: number) => void
  onClose: () => void
}) {
  const [durationS, setDurationS] = useState(15)
  const [maxCards, setMaxCards] = useState(6)
  const [moves, setMoves] = useState<MoveFilter>('both')
  const [mode, setMode] = useState<BoardMode>('auto')
  const [anchor, setAnchor] = useState<'start' | 'end'>('end')
  const [detail, setDetail] = useState<Detail>('pass')
  const [pinned, setPinned] = useState(false)
  // Display & drawing config (all toggleable — stills vs animation, etc.).
  const [animate, setAnimate] = useState(false)
  const [overlay, setOverlay] = useState<OverlayCfg>(
    { control: false, block: false, lanes: false, pressure: false })
  const [flipH, setFlipH] = useState(false)
  const [flipV, setFlipV] = useState(false)
  const [showStats, setShowStats] = useState(true)
  const [showDisplay, setShowDisplay] = useState(false)
  const [tool, setTool] = useState<DrawTool>('none')
  const [annos, setAnnos] = useState<Record<number, Anno[]>>({})
  const [spotlight, setSpotlight] = useState<Set<string>>(() => new Set())
  const [title, setTitle] = useState('')
  const [frames, setFrames] = useState<TacFrame[]>([])
  const [cards, setCards] = useState<Card[]>([])
  const [sel, setSel] = useState(0)
  const [preview, setPreview] = useState<number | null>(null)
  const [playing, setPlaying] = useState(false)
  const [loading, setLoading] = useState(false)
  const [generated, setGenerated] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [entered, setEntered] = useState(false)
  const svgRefs = useRef<(SVGSVGElement | null)[]>([])
  const cardEls = useRef<(HTMLDivElement | null)[]>([])
  const onSeekRef = useRef(onSeek)
  useEffect(() => { onSeekRef.current = onSeek }, [onSeek])
  // Abort an in-flight tracking read when the board closes or re-generates —
  // the R2 read is slow, so a stale request must not land after a new one.
  const abortRef = useRef<AbortController | null>(null)
  useEffect(() => () => abortRef.current?.abort(), [])

  const colorFor = useCallback(
    (team: number) => (team === 1 ? homeColor : awayColor),
    [homeColor, awayColor])

  // Slide the panel in from the right on open.
  useEffect(() => {
    const r = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(r)
  }, [])

  // Pinned: dock the panel and push the page content left to make room, so it
  // sits beside the video instead of over it. Restored on unpin or close.
  useEffect(() => {
    if (!pinned) return
    const body = document.body
    const prevPad = body.style.paddingRight, prevTrans = body.style.transition
    body.style.transition = 'padding-right 0.22s ease'
    body.style.paddingRight = `${PANEL_W}px`
    // Restore whatever was there before — never blank it, so another
    // component's padding/transition survives unpin or close.
    return () => { body.style.paddingRight = prevPad; body.style.transition = prevTrans }
  }, [pinned])

  // Turn a window of frames into a deck for the chosen mode, carrying any
  // captions the coach already wrote (matched by frame index).
  const buildCards = useCallback(
    (fr: TacFrame[], m: BoardMode, max: number, holdS: number, prev: Card[]): Card[] => {
      if (fr.length === 0) return []
      const last = fr.length - 1
      const cap = (idx: number) => prev.find((c) => c.idx === idx)?.caption ?? ''
      if (m === 'manual') {
        // Seed from whatever is on screen (so you can hand-tune an auto deck),
        // else just the two ends to start adding from.
        const base: Card[] = prev.length ? prev
          : [{ idx: 0, kind: 'start', caption: '' },
             { idx: last, kind: 'end', caption: '' }]
        const seen = new Set<number>()
        const out: Card[] = []
        for (const c of [...base].sort((a, b) => a.idx - b.idx)) {
          const idx = Math.min(last, Math.max(0, c.idx))
          if (seen.has(idx)) continue
          seen.add(idx)
          out.push({ ...c, idx })
        }
        if (!seen.has(0)) out.unshift({ idx: 0, kind: 'start', caption: cap(0) })
        if (!seen.has(last)) out.push({ idx: last, kind: 'end', caption: cap(last) })
        return out.sort((a, b) => a.idx - b.idx)
      }
      const evIdx: KeyFrame[] = events
        .map((e) => ({ idx: nearestFrameIndex(fr, e.frame_idx), kind: eventKind(e.type) }))
        .filter((e) => e.idx >= 0)
      // Auto shows every beat up to the AUTO_MAX ceiling (then thins toward the
      // kept end) so you can remove what you don't want; even mode draws
      // exactly `max` cards at fixed intervals.
      const keys = m === 'auto'
        ? detectKeyFrames(fr, evIdx, fps, AUTO_MAX, anchor, holdS)
        : evenKeyFrames(fr.length, max)
      return keys.map((k) => ({ ...k, caption: cap(k.idx) }))
    }, [events, fps, anchor])

  // The coach clicks Generate to build (or rebuild) the deck — no auto-run.
  const generate = useCallback(async () => {
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl
    let timedOut = false
    const timer = setTimeout(() => { timedOut = true; ctrl.abort() }, 30000)
    // A fresh deck: drop the old drawings (and, via the empty previous list
    // below, the old captions) so neither can re-bind to a different clip.
    setAnnos({})
    setLoading(true); setError(null); setPlaying(false); setPreview(null)
    // The playhead is the clip's start (look ahead) or its end (look back).
    const want = Math.min(MAX_FRAMES, Math.max(1, Math.round(durationS * fps)))
    const start = anchor === 'end' ? Math.max(0, startFrame - want + 1) : startFrame
    const count = anchor === 'end' ? startFrame - start + 1 : want
    try {
      const raw = await api<RawFrame[]>(
        `/api/v1/opsqa/games/${gameId}/tracking-frames/${start}/${count}`,
        { signal: ctrl.signal })
      if (ctrl.signal.aborted) return
      const fr = (Array.isArray(raw) ? raw : []).map(cleanFrame)
      setGenerated(true)
      if (fr.length === 0) {
        setFrames([]); setCards([]); setError('No tracking data for this segment.')
        return
      }
      setFrames(fr)
      setCards(buildCards(fr, mode, maxCards, detailHoldS(detail), []))
      setSel(0)
    } catch (e) {
      if (ctrl.signal.aborted) {
        if (timedOut) { setGenerated(true); setError('Timed out loading tracking — try a shorter clip.') }
        return
      }
      if (e instanceof DOMException && e.name === 'AbortError') return
      setFrames([]); setCards([]); setGenerated(true)
      setError('Could not load tracking for this segment.')
    } finally {
      clearTimeout(timer)
      // Clear loading unless a newer generate has taken over (it owns it now).
      if (abortRef.current === ctrl) setLoading(false)
    }
  }, [gameId, startFrame, fps, durationS, anchor, mode, maxCards, detail, buildCards])

  // Re-cut the existing window when mode / detail / count changes — no refetch.
  const recut = useCallback((m: BoardMode, max: number, holdS: number) => {
    setPlaying(false); setPreview(null); setSel(0)
    setCards((prev) => frames.length ? buildCards(frames, m, max, holdS, prev) : prev)
  }, [frames, buildCards])

  // Auto-advance when playing, looping back to the first card at the end.
  useEffect(() => {
    if (!playing || cards.length < 2) return
    const t = setInterval(
      () => setSel((c) => (c + 1) % cards.length), 1600)
    return () => clearInterval(t)
  }, [playing, cards.length])

  const seek = useCallback((i: number) => {
    const c = cards[i]
    const fr = c ? frames[c.idx] : undefined
    if (fr && onSeekRef.current) onSeekRef.current(fr.frame)
  }, [cards, frames])

  const go = useCallback((delta: number) => {
    setPlaying(false); setPreview(null)
    const n = Math.min(cards.length - 1, Math.max(0, sel + delta))
    setSel(n); seek(n)
  }, [cards.length, sel, seek])

  // Coach work that only lives in component state until Download Deck — losing
  // it to a stray Escape is the expensive mistake, so closing confirms first.
  const hasUnsavedWork = cards.some((c) => c.caption.trim() || c.kind === 'manual')
    || Object.values(annos).some((a) => a.length > 0)
  const requestClose = useCallback(() => {
    if (hasUnsavedWork && !window.confirm(
      'Close the tactics board? Captions and drawings are not saved — Download Deck first to keep them.')) return
    onClose()
  }, [hasUnsavedWork, onClose])

  // Keyboard: arrows step, space plays/pauses, Escape closes. The board is a
  // modal, so for the keys it owns it stops propagation (capture phase) — a
  // page-level listener (Match Lab's) must not also act on them.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      const typing = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')
      if (typing) {
        // Escape leaves the field; it must not close the board and lose the
        // deck mid-edit of a title or caption.
        if (e.key === 'Escape') el!.blur()
        return
      }
      if (e.key === 'Escape') { e.stopImmediatePropagation(); requestClose(); return }
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== ' ') return
      e.preventDefault(); e.stopImmediatePropagation()
      if (e.key === 'ArrowLeft') go(-1)
      else if (e.key === 'ArrowRight') go(1)
      else setPlaying((p) => !p)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [go, requestClose])

  // Keep the selected card scrolled into view as you step or play through.
  useEffect(() => {
    cardEls.current[sel]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [sel])

  const selectCard = (i: number) => {
    setPlaying(false); setPreview(null); setSel(i); seek(i)
  }

  const setCaption = (i: number, text: string) =>
    setCards((cs) => cs.map((c, j) => (j === i ? { ...c, caption: text } : c)))

  const addCard = () => {
    if (frames.length === 0) return
    const idx = preview ?? (cards[sel]?.idx ?? 0)
    setPreview(null)
    if (cards.some((c) => c.idx === idx)) return
    const nextCards = [...cards, { idx, kind: 'manual' as KeyKind, caption: '' }]
      .sort((a, b) => a.idx - b.idx)
    setCards(nextCards)
    setSel(nextCards.findIndex((c) => c.idx === idx))
  }

  const removeCard = (i: number) => {
    if (cards.length <= 1) return
    const gone = cards[i]?.idx
    setCards((cs) => cs.filter((_, j) => j !== i))
    // Drop its drawings too, else re-adding a card at the same frame index
    // resurrects annotations the coach deleted.
    if (gone !== undefined) setAnnos((m) => {
      if (!(gone in m)) return m
      const n = { ...m }; delete n[gone]; return n
    })
    // Removing a card ABOVE the selected one shifts it down — keep the same
    // card highlighted by decrementing.
    setSel((s) => Math.max(0, Math.min(i < s ? s - 1 : s, cards.length - 2)))
  }

  const downloadCard = (i: number) => {
    const svg = svgRefs.current[i]
    if (!svg) return
    const xml = new XMLSerializer().serializeToString(svg)
    const img = new Image()
    const url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml)
    img.onerror = () => setError('Could not export that card as an image.')
    img.onload = () => {
      const scale = 10 // ~1110 × 800 px, crisp for a slide deck.
      const canvas = document.createElement('canvas')
      canvas.width = VIEW_W * scale
      canvas.height = VIEW_H * scale
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      canvas.toBlob((blob) => {
        if (!blob) return
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = `tactics-${gameId}-card-${i + 1}.png`
        a.click()
        setTimeout(() => URL.revokeObjectURL(a.href), 0) // after the download starts
      })
    }
    img.src = url
  }

  const addAnno = (i: number, a: Anno) =>
    setAnnos((m) => ({ ...m, [i]: [...(m[i] ?? []), a] }))
  const clearAnnos = (i: number) => setAnnos((m) => ({ ...m, [i]: [] }))
  const toggleSpot = (key: string) => setSpotlight((s) => {
    const n = new Set(s)
    if (n.has(key)) n.delete(key); else n.add(key)
    return n
  })

  const metrics = useMemo(
    () => (frames.length ? sequenceMetrics(frames, fps, detailHoldS(detail)) : null),
    [frames, fps, detail])

  // Step 1 of promote: snapshot the deck to the portable JSON and download it
  // for inspection. The backend Save & Promote will POST this same document.
  const downloadDeck = () => {
    const doc = serializeDeck({
      gameId, title, homeName, awayName, homeColor, awayColor,
      flipH, flipV, cards, frames, fps, moves, annos,
    })
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `tactics-deck-${gameId}-${Date.now()}.json`
    a.click()
    setTimeout(() => URL.revokeObjectURL(a.href), 0) // after the download starts
  }

  const btn: CSSProperties = {
    background: 'none', border: '1px solid #555', color: '#ddd',
    fontSize: '12px', cursor: 'pointer', padding: '3px 10px', borderRadius: '4px',
  }
  const field = (w: string): CSSProperties => ({
    background: '#0e1116', color: '#ddd', border: '1px solid #333',
    borderRadius: '4px', padding: '2px 4px', width: w,
  })

  return (
    <div role="dialog" aria-label="Coach's Tactics Board" aria-modal={false}
         style={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1000,
                  width: 'min(460px, 96vw)', background: '#15181e',
                  borderLeft: '1px solid #2a2e36',
                  boxShadow: '-8px 0 24px rgba(0,0,0,0.45)',
                  display: 'flex', flexDirection: 'column',
                  transform: entered ? 'translateX(0)' : 'translateX(100%)',
                  transition: 'transform 0.22s ease' }}>

      {/* ── Sticky header: title, controls, transport ── */}
      <div style={{ flex: '0 0 auto', padding: '12px 14px',
                    borderBottom: '1px solid #23272f' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span aria-hidden style={{ fontSize: '18px' }}>{'⚽'}</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder="Name this move" aria-label="Board title"
                 style={{ flex: 1, background: '#0e1116', color: '#fff',
                          border: '1px solid #333', borderRadius: '4px',
                          padding: '5px 8px', fontSize: '14px', fontWeight: 600 }} />
          <button onClick={() => setPinned((p) => !p)}
                  aria-label={pinned ? 'Unpin panel' : 'Pin panel'}
                  title={pinned ? 'Unpin — overlay the page' : 'Pin — push the page left'}
                  style={{ ...btn, border: 'none', fontSize: '15px',
                           color: pinned ? '#8ab4ff' : '#999' }}>{'\u{1F4CC}'}</button>
          <button onClick={requestClose} aria-label="Close tactics board"
                  style={{ ...btn, border: 'none', fontSize: '18px' }}>{'✕'}</button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px',
                      alignItems: 'center', fontSize: '12px', color: '#aaa',
                      marginTop: '10px' }}>
          <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            Length
            <input type="range" min={5} max={60} step={1} value={durationS}
                   onChange={(e) => setDurationS(Number(e.target.value))} />
            <span style={{ color: '#ddd', width: '30px' }}>{durationS}s</span>
          </label>
          <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            Clip
            <select value={anchor}
                    onChange={(e) => setAnchor(e.target.value as 'start' | 'end')}
                    style={field('auto')} aria-label="Clip anchor">
              <option value="end">Ends here (look back)</option>
              <option value="start">Starts here (look ahead)</option>
            </select>
          </label>
          <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            Cards
            <select value={mode}
                    onChange={(e) => { const m = e.target.value as BoardMode; setMode(m); recut(m, maxCards, detailHoldS(detail)) }}
                    style={field('auto')} aria-label="Card mode">
              <option value="auto">Auto — every beat</option>
              <option value="manual">Manual — click to add</option>
              <option value="even">Even spacing</option>
            </select>
          </label>
          {mode === 'auto' && (
            <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              Detail
              <select value={detail}
                      onChange={(e) => { const d = e.target.value as Detail; setDetail(d); recut(mode, maxCards, detailHoldS(d)) }}
                      style={field('auto')} aria-label="Detail">
                <option value="touch">Every touch</option>
                <option value="pass">Each pass</option>
                <option value="settled">Settled only</option>
              </select>
            </label>
          )}
          {mode === 'even' && (
            <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              Count
              <input type="range" min={2} max={12} step={1} value={maxCards}
                     onChange={(e) => { const v = Number(e.target.value); setMaxCards(v); recut(mode, v, detailHoldS(detail)) }} />
              <span style={{ color: '#ddd', width: '16px' }}>{maxCards}</span>
            </label>
          )}
          <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            Arrows
            <select value={moves} onChange={(e) => setMoves(e.target.value as MoveFilter)}
                    style={field('auto')}>
              <option value="both">Both teams</option>
              <option value="home">{homeName} only</option>
              <option value="away">{awayName} only</option>
            </select>
          </label>
          <button onClick={() => void generate()} disabled={loading}
                  style={{ ...btn, borderColor: '#2563eb', color: '#8ab4ff' }}>
            {loading ? 'Loading…' : generated ? 'Regenerate' : 'Generate'}
          </button>
          <button onClick={() => setShowDisplay((s) => !s)}
                  aria-label="Display options" title="Display & drawing options"
                  style={{ ...btn, color: showDisplay ? '#8ab4ff' : '#ccc' }}>
            {'⚙'} Display
          </button>
          <button onClick={downloadDeck} disabled={cards.length === 0}
                  aria-label="Download deck JSON"
                  title="Download this deck as JSON (the promote format)"
                  style={{ ...btn }}>{'⬇'} Deck</button>
          <span style={{ opacity: 0.6 }}>
            {anchor === 'end' ? 'to' : 'from'} f{startFrame}
          </span>
        </div>

        {showDisplay && (
          <DisplayPanel
            animate={animate} setAnimate={setAnimate}
            overlay={overlay} setOverlay={setOverlay}
            flipH={flipH} setFlipH={setFlipH} flipV={flipV} setFlipV={setFlipV}
            showStats={showStats} setShowStats={setShowStats}
            tool={tool} setTool={setTool}
            spotlightCount={spotlight.size} clearSpotlight={() => setSpotlight(new Set())}
            field={field} btn={btn} />
        )}

        {cards.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
                        marginTop: '10px' }}>
            <button onClick={() => go(-1)} disabled={sel === 0}
                    aria-label="Previous card" style={btn}>{'◀'}</button>
            <button onClick={() => setPlaying((p) => !p)} disabled={cards.length < 2}
                    aria-label={playing ? 'Pause' : 'Play'}
                    style={{ ...btn, color: '#fff' }}>
              {playing ? '⏸ Pause' : '▶ Play'}
            </button>
            <button onClick={() => go(1)} disabled={sel >= cards.length - 1}
                    aria-label="Next card" style={btn}>{'▶'}</button>
            <span style={{ fontSize: '12px', color: '#999', marginLeft: 'auto' }}>
              {sel + 1} / {cards.length} cards
            </span>
          </div>
        )}
      </div>

      {/* ── Scrollable deck, cards stacked top to bottom ── */}
      <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: '10px 14px' }}>
        {error && (
          <p style={{ color: '#e88', fontSize: '12px', margin: '20px 4px',
                      textAlign: 'center' }}>{error}</p>
        )}

        {!generated && !error && (
          <p style={{ color: '#8a9099', fontSize: '13px', margin: '28px 8px',
                      textAlign: 'center', lineHeight: 1.5 }}>
            Set the length and how the cards are cut,<br />
            then <strong style={{ color: '#8ab4ff' }}>Generate</strong> to build the deck.
          </p>
        )}

        {generated && !error && frames.length > 0 && (
          <>
            {showStats && metrics && <StatsPanel metrics={metrics} />}
            {mode === 'manual' && (() => {
              const pIdx = preview ?? (cards[sel]?.idx ?? 0)
              const pf = frames[pIdx]
              const secs = pf ? ((pf.frame - frames[0]!.frame) / fps).toFixed(1) : '0.0'
              return (
                <div style={{ marginBottom: '14px', padding: '6px',
                              border: '1px dashed #3a4150', borderRadius: '6px' }}>
                  <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>
                    Scrub, then add a card
                  </div>
                  {pf && (
                    <MiniPitch frame={pf} moves={moves} colorFor={colorFor}
                               caption="" label="Preview"
                               homeName={homeName} awayName={awayName}
                               flipH={flipH} flipV={flipV} overlay={overlay}
                               animate={false} isActive={false}
                               annos={NO_ANNOS} tool="none" spotlight={spotlight} />
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
                                marginTop: '6px' }}>
                    <input type="range" min={0} max={Math.max(0, frames.length - 1)}
                           step={1} value={pIdx} aria-label="Scrub window"
                           onChange={(e) => setPreview(Number(e.target.value))}
                           style={{ flex: 1 }} />
                    <span style={{ fontSize: '11px', color: '#999', width: '40px' }}>{secs}s</span>
                    <button onClick={addCard}
                            style={{ ...btn, borderColor: '#2563eb', color: '#8ab4ff' }}>
                      {'➕'} Add
                    </button>
                  </div>
                </div>
              )
            })()}

            {cards.map((c, i) => {
              const fr = frames[c.idx]!
              const nx = cards[i + 1] ? frames[cards[i + 1]!.idx] : undefined
              const meta = KIND_META[c.kind]
              const secs = ((fr.frame - frames[0]!.frame) / fps).toFixed(1)
              const commentary = i === 0 ? 'Starting shape.'
                : describeTransition(frames[cards[i - 1]!.idx]!, fr, c.kind, fps,
                                     homeName, awayName)
              return (
                <div key={c.idx} ref={(el) => { cardEls.current[i] = el }}
                     style={{ marginBottom: '12px', borderRadius: '6px',
                              border: `1px solid ${i === sel ? '#2563eb' : '#23272f'}`,
                              background: '#12151a', padding: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px',
                                fontSize: '11px', marginBottom: '4px' }}>
                    <span style={{ color: meta.color }}>{meta.glyph} {i + 1}</span>
                    <span style={{ color: '#777' }}>{meta.label} {'·'} {secs}s</span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
                      <button onClick={() => downloadCard(i)}
                              aria-label={`Download card ${i + 1}`}
                              style={{ ...btn, padding: '1px 6px', fontSize: '11px' }}>{'⬇'}</button>
                      {(annos[c.idx]?.length ?? 0) > 0 && (
                        <button onClick={() => clearAnnos(c.idx)}
                                aria-label={`Clear drawing on card ${i + 1}`}
                                title="Clear drawing"
                                style={{ ...btn, padding: '1px 6px', fontSize: '11px' }}>{'⌫'}</button>
                      )}
                      {cards.length > 1 && (
                        <button onClick={() => removeCard(i)}
                                aria-label={`Remove card ${i + 1}`}
                                title="Remove from the narrative"
                                style={{ ...btn, padding: '1px 6px', fontSize: '11px' }}>{'✕'}</button>
                      )}
                    </span>
                  </div>
                  <MiniPitch frame={fr} next={nx} moves={moves} colorFor={colorFor}
                             caption={c.caption} label={`Card ${i + 1}`}
                             homeName={homeName} awayName={awayName}
                             flipH={flipH} flipV={flipV} overlay={overlay}
                             animate={animate} isActive={i === sel}
                             framesSlice={frames.slice(c.idx, (cards[i + 1]?.idx ?? c.idx) + 1)}
                             annos={annos[c.idx] ?? NO_ANNOS} tool={tool}
                             onDraw={(a) => addAnno(c.idx, a)}
                             spotlight={spotlight} onSpotlight={toggleSpot}
                             onClick={() => selectCard(i)}
                             innerRef={(el) => { svgRefs.current[i] = el }} />
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'baseline',
                                marginTop: '5px' }}>
                    <p style={{ flex: 1, margin: 0, fontSize: '11px', color: '#9aa4b0',
                                lineHeight: 1.35 }}>{commentary}</p>
                    {i > 0 && (
                      <button onClick={() => setCaption(i, commentary)}
                              title="Use as the slide caption"
                              aria-label={`Use commentary as caption for card ${i + 1}`}
                              style={{ ...btn, padding: '0 5px', fontSize: '11px' }}>{'↳'}</button>
                    )}
                  </div>
                  <input value={c.caption} onChange={(e) => setCaption(i, e.target.value)}
                         placeholder={`Card ${i + 1}: what is happening here?`}
                         aria-label={`Card ${i + 1} caption`}
                         style={{ width: '100%', marginTop: '6px', background: '#0e1116',
                                  color: '#fff', border: '1px solid #333', borderRadius: '4px',
                                  padding: '5px 7px', fontSize: '12px', boxSizing: 'border-box' }} />
                </div>
              )
            })}
          </>
        )}
      </div>
    </div>
  )
}

/** One card: a mini pitch with the players, the ball, and the movement arrows
 *  into the next card. Reused for every card in the deck and the manual-mode
 *  scrub preview. */
function MiniPitch({ frame, next, moves, colorFor, homeName, awayName,
                     caption, label, flipH, flipV, overlay, animate, isActive,
                     framesSlice, annos, tool, onDraw, spotlight, onSpotlight,
                     onClick, innerRef }: {
  frame: TacFrame
  next?: TacFrame
  moves: MoveFilter
  colorFor: (team: number) => string
  homeName: string
  awayName: string
  caption: string
  label: string
  flipH: boolean
  flipV: boolean
  overlay: OverlayCfg
  animate: boolean
  isActive: boolean
  framesSlice?: TacFrame[]
  annos: Anno[]
  tool: DrawTool
  onDraw?: (a: Anno) => void
  spotlight: Set<string>
  onSpotlight?: (key: string) => void
  onClick?: () => void
  innerRef?: (el: SVGSVGElement | null) => void
}) {
  const svgLocal = useRef<SVGSVGElement | null>(null)
  const [animIdx, setAnimIdx] = useState(0)
  const [draft, setDraft] = useState<Anno | null>(null)

  // Animation: step through this card's own frames on a loop when it's the
  // selected card, striding so any gap length plays in roughly 2.5 s.
  const canAnimate = animate && isActive && !!framesSlice && framesSlice.length > 1
  useEffect(() => {
    if (!canAnimate) { setAnimIdx(0); return }
    const len = framesSlice!.length
    const stride = Math.max(1, Math.round(len / 50))
    let raf = 0, last = 0, i = 0
    const tick = (t: number) => {
      if (t - last > 50) { last = t; i = (i + stride) % len; setAnimIdx(i) }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
    // Keyed on the slice's shape, not its array identity, so an unrelated
    // parent re-render (e.g. typing a caption) does not restart the loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAnimate, framesSlice?.length, framesSlice?.[0]?.frame])

  const shown = canAnimate ? (framesSlice![animIdx] ?? frame) : frame

  // Movement arrows belong to stills; motion tells the story when playing.
  const moveArrows = !canAnimate && next
    ? playerMovements(frame, next)
        .filter((m) => moves === 'both' || (moves === 'home') === (m.team === 1))
        .map((m) => ({ team: m.team, from: m.from, to: m.to }))
    : []
  const ballMove = !canAnimate && next?.ball && frame.ball
    && Math.hypot(next.ball.x - frame.ball.x, next.ball.y - frame.ball.y) >= 100
    ? { from: { x: frame.ball.x, y: frame.ball.y }, to: { x: next.ball.x, y: next.ball.y } } : null

  const carrier = useMemo(() => ((overlay.lanes || overlay.pressure)
    ? nearestToBall(shown) : null), [overlay.lanes, overlay.pressure, shown])
  const ov: Overlay = useMemo(() => {
    const o: Overlay = {}
    if (overlay.control) o.cells = controlGrid(shown.players)
    if (overlay.block) o.blocks = [1, 0].map((t) => ({ team: t, hull: teamBlock(shown.players, t) }))
    if (overlay.lanes && carrier) {
      o.lanes = openLanes(carrier, shown.players.filter((p) => p.team === carrier.team),
          shown.players.filter((p) => p.team !== carrier.team))
        .map((t) => ({ from: { x: carrier.x, y: carrier.y }, to: { x: t.x, y: t.y } }))
    }
    if (overlay.pressure && carrier) {
      o.carrier = carrier
      o.press = pressureCm({ x: carrier.x, y: carrier.y },
        shown.players.filter((p) => p.team !== carrier.team).map((p) => ({ x: p.x, y: p.y })))
    }
    return o
  }, [overlay, carrier, shown])

  const teams: RenderTeams = {
    home: { name: homeName, color: colorFor(1) },
    away: { name: awayName, color: colorFor(0) },
  }
  const renderCard: RenderCard = {
    players: shown.players, ball: shown.ball, moves: moveArrows, ballMove, caption,
    annos: draft ? [...annos, draft] : annos,
  }
  const html = cardInner(renderCard, teams, { flipH, flipV, spotlight, overlay: ov })

  const drawing = tool === 'arrow' || tool === 'zone' || tool === 'text'
  const svgPoint = (e: ReactPointerEvent<SVGSVGElement>) => {
    const svg = svgLocal.current
    if (!svg) return null
    const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY
    const ctm = svg.getScreenCTM(); if (!ctm) return null
    const p = pt.matrixTransform(ctm.inverse())
    // Store the un-flipped point: cardInner re-applies the current flip, so a
    // drawing stays put under the pointer now and tracks the pitch if flipped.
    return { x: flipH ? PITCH_L - p.x : p.x, y: flipV ? PITCH_W - p.y : p.y }
  }
  const onPointerDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!drawing) return
    const p = svgPoint(e); if (!p) return
    e.stopPropagation()
    if (tool === 'text') {
      const text = window.prompt('Label text')?.trim()
      if (text) onDraw?.({ kind: 'text', x: p.x, y: p.y, text })
      return
    }
    svgLocal.current?.setPointerCapture?.(e.pointerId)
    setDraft(tool === 'arrow'
      ? { kind: 'arrow', x1: p.x, y1: p.y, x2: p.x, y2: p.y }
      : { kind: 'zone', x: p.x, y: p.y, w: 0, h: 0 })
  }
  const onPointerMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!draft) return
    const p = svgPoint(e); if (!p) return
    setDraft(draft.kind === 'arrow'
      ? { ...draft, x2: p.x, y2: p.y }
      : { kind: 'zone', x: Math.min(draft.x, p.x), y: Math.min(draft.y, p.y),
          w: Math.abs(p.x - draft.x), h: Math.abs(p.y - draft.y) })
  }
  const onPointerUp = () => {
    if (!draft) return
    const ok = draft.kind === 'arrow'
      ? Math.hypot(draft.x2 - draft.x1, draft.y2 - draft.y1) > 2
      : draft.kind === 'zone' ? draft.w > 2 && draft.h > 2 : false
    if (ok) onDraw?.(draft)
    setDraft(null)
  }
  // Players are injected HTML, so spotlight clicks delegate via data-key.
  const onSvgClick = (e: ReactMouseEvent<SVGSVGElement>) => {
    if (tool === 'spotlight') {
      const key = (e.target as Element).closest?.('[data-key]')?.getAttribute('data-key')
      if (key) { onSpotlight?.(key); return }
    }
    if (tool === 'none') onClick?.()
  }

  const setRef = (el: SVGSVGElement | null) => { svgLocal.current = el; innerRef?.(el) }

  return (
    // The card body is the shared renderer (tacticsCardSvg) — the one source
    // the PNG export and the club viewer also draw from; only the pointer
    // handlers live here.
    <svg ref={setRef} viewBox={VIEW_BOX} role="img" aria-label={label}
         onClick={onSvgClick}
         onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
         style={{ display: 'block', width: '100%', borderRadius: '4px',
                  touchAction: drawing ? 'none' : undefined,
                  cursor: drawing ? 'crosshair'
                    : tool === 'spotlight' ? 'pointer' : onClick ? 'pointer' : 'default' }}
         dangerouslySetInnerHTML={{ __html: html }} />
  )
}
// One-line hint for each drawing tool, shown live under the Draw row.
const DRAW_HINT: Record<DrawTool, string> = {
  none: 'Drawing off — click a card to select it and seek the video.',
  arrow: 'Drag on a card to draw an arrow.',
  zone: 'Drag on a card to shade a zone.',
  text: 'Click on a card to drop a text label.',
  spotlight: 'Click players to highlight them across the whole deck.',
}

/** The display & drawing options panel (all toggles live here), each with a
 *  hover tooltip explaining what it does. */
function DisplayPanel({
  animate, setAnimate, overlay, setOverlay,
  flipH, setFlipH, flipV, setFlipV, showStats, setShowStats, tool, setTool,
  spotlightCount, clearSpotlight, field, btn,
}: {
  animate: boolean; setAnimate: (b: boolean) => void
  overlay: OverlayCfg; setOverlay: (o: OverlayCfg) => void
  flipH: boolean; setFlipH: (b: boolean) => void
  flipV: boolean; setFlipV: (b: boolean) => void
  showStats: boolean; setShowStats: (b: boolean) => void
  tool: DrawTool; setTool: (t: DrawTool) => void
  spotlightCount: number; clearSpotlight: () => void
  field: (w: string) => CSSProperties; btn: CSSProperties
}) {
  const chip = (on: boolean): CSSProperties => ({
    ...btn, padding: '2px 7px', fontSize: '11px',
    borderColor: on ? '#2563eb' : '#444', color: on ? '#8ab4ff' : '#bbb',
    background: on ? 'rgba(37,99,235,0.15)' : 'none',
  })
  const row: CSSProperties = { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }
  const lab: CSSProperties = { fontSize: '11px', color: '#8a9099', width: '46px', cursor: 'help' }
  const set = (k: keyof OverlayCfg) => setOverlay({ ...overlay, [k]: !overlay[k] })
  return (
    <div style={{ marginTop: '10px', padding: '8px', borderRadius: '6px',
                  background: '#0e1116', display: 'flex', flexDirection: 'column', gap: '7px' }}>
      <div style={row}>
        <span style={lab} title="How each card is drawn">View</span>
        <button style={chip(!animate)} onClick={() => setAnimate(false)}
                title="Show each card as a still snapshot.">Stills</button>
        <button style={chip(animate)} onClick={() => setAnimate(true)}
                title="Play the selected card's move as a short animation; other cards stay stills.">Animate</button>
      </div>
      <div style={row}>
        <span style={lab} title="Extra layers drawn on the pitch">Overlays</span>
        <button style={chip(overlay.control)} onClick={() => set('control')}
                title="Pitch control — shade each zone by whichever team's nearest player covers it.">Space</button>
        <button style={chip(overlay.block)} onClick={() => set('block')}
                title="Draw each team's shape as a hull — how compact or stretched they are.">Block</button>
        <button style={chip(overlay.lanes)} onClick={() => set('lanes')}
                title="Highlight the open passing options from the player on the ball.">Lanes</button>
        <button style={chip(overlay.pressure)} onClick={() => set('pressure')}
                title="Ring the ball carrier by how close the nearest defender is (red = tight).">Pressure</button>
      </div>
      <div style={row}>
        <span style={lab} title="Mirror the pitch to suit your view">Orient</span>
        <button style={chip(flipH)} onClick={() => setFlipH(!flipH)}
                title="Mirror left–right — e.g. make the attack run to the right.">Flip ↔</button>
        <button style={chip(flipV)} onClick={() => setFlipV(!flipV)}
                title="Mirror top–bottom — e.g. put your team at the bottom.">Flip ↕</button>
        <button style={chip(showStats)} onClick={() => setShowStats(!showStats)}
                title="Show the sequence stats panel (duration, passes, distance, formation).">Stats</button>
      </div>
      <div style={row}>
        <span style={lab} title="Draw your own markings on a card">Draw</span>
        <select value={tool} onChange={(e) => setTool(e.target.value as DrawTool)}
                style={field('auto')} aria-label="Drawing tool" title={DRAW_HINT[tool]}>
          <option value="none">Off</option>
          <option value="arrow">Arrow</option>
          <option value="zone">Zone</option>
          <option value="text">Text</option>
          <option value="spotlight">Spotlight</option>
        </select>
        {spotlightCount > 0 && (
          <button style={{ ...btn, padding: '2px 7px', fontSize: '11px' }}
                  onClick={clearSpotlight}
                  title="Clear the highlighted players.">Clear spotlight ({spotlightCount})</button>
        )}
      </div>
      <p style={{ margin: '1px 0 0', fontSize: '10px', color: '#6b7280' }}>{DRAW_HINT[tool]}</p>
    </div>
  )
}

/** Deck-level readout of what the sequence achieved — all direction-agnostic
 *  (formation needs an attacking direction, which the board does not resolve;
 *  that is a follow-up routed through the direction source of truth). */
function StatsPanel({ metrics }: { metrics: SequenceMetrics }) {
  const stat = (v: string) => <strong style={{ color: '#dfe3e8' }}>{v}</strong>
  return (
    <div style={{ marginBottom: '12px', padding: '7px 9px', borderRadius: '6px',
                  background: '#12151a', border: '1px solid #23272f',
                  fontSize: '11px', color: '#9aa4b0', lineHeight: 1.7 }}>
      <div>
        {stat(metrics.durationS.toFixed(1) + 's')} · {stat(String(metrics.passes))} passes ·
        ball {stat(Math.round(metrics.ballMoveM) + 'm')} · top {stat(Math.round(metrics.topSpeedKmh) + ' km/h')}
        {metrics.enteredBox && <> · {stat('⚽ box entry')}</>}
      </div>
    </div>
  )
}